import { isValid, parse } from "date-fns";
import { TFunction } from "i18next";
import * as Yup from "yup";

import { InsuranceNumberSchema, WithInsuranceNumberAndBirthday } from "./ClientInsuranceNumberField.types";

const SSN_PREFIX_LENGTH = 4;

export function formatInsuranceNumber(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  return value.length > SSN_PREFIX_LENGTH
    ? `${value.slice(0, SSN_PREFIX_LENGTH)} ${value.slice(SSN_PREFIX_LENGTH)}`
    : value;
}

// `clients.create.invalidInsuranceNumber` also exists in the internal app's own namespace: internal
// callers pass their app `t`, so the key resolves there; shared callers pass the shared-namespace `t`.
export const insuranceNumberValidation = (t: TFunction): InsuranceNumberSchema => ({
  insuranceNumber: Yup.string()
    .nullable()
    .optional()
    .test("insurance-number", t("clients.create.invalidInsuranceNumber"), function (value) {
      if (!value) {
        return true;
      }

      if (/^[Xx]{4}$/.test(value)) {
        return true;
      }

      if (/^\d{10}$/.test(value)) {
        return isValidSvnr(value);
      }

      if (/^\d{4}$/.test(value)) {
        return isValidSvnr(value, (this.parent as WithInsuranceNumberAndBirthday).birthday ?? undefined);
      }

      return false;
    }),
});

export const formatDate = (datePart: string): Date | null => {
  const yy = parseInt(datePart.slice(SSN_PREFIX_LENGTH), 10);
  const yyyy = 2000 + yy > new Date().getFullYear() ? 1900 + yy : 2000 + yy;
  const date = parse(`${datePart.slice(0, 4)}${yyyy}`, "ddMMyyyy", new Date());
  return isValid(date) ? date : null;
};

const calculateChecksum = (svnr: string): number => {
  const weights = [3, 7, 9, 0, 5, 8, 4, 2, 1, 6];
  let sum = 0;

  for (let i = 0; i < weights.length; i++) {
    sum += (weights[i] ?? 0) * parseInt(svnr.charAt(i));
  }
  sum %= 11;

  return sum;
};

export const isValidSvnr = (svnr: string, date?: Date): boolean => {
  let fullSvnr = svnr;

  if (svnr.length === 4) {
    if (!date) {
      return false;
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);

    fullSvnr = svnr + day + month + year;
  }

  if (fullSvnr.length !== 10 || !/^[0-9]+$/.test(fullSvnr) || fullSvnr.startsWith("0")) {
    return false;
  }

  return calculateChecksum(fullSvnr) === parseInt(fullSvnr.charAt(3));
};
